<template>
  <div
    class="modal-activator"
    :class="variantClass"
  >
    <slot v-bind="bindings" />
    <transition :name="transition">
      <div
        v-if="isActive"
        ref="modalOverlay"
        class="modal-overlay"
      >
        <div
          ref="modalWrapper"
          class="modal-wrapper"
          :class="[{ active: isActive }, { mounted: this.isMounted }, customClass]"
          :role="isAlertDialog ? 'alertdialog' : 'dialog'"
          aria-modal="true"
          :aria-labelledby="ariaLabelledBy"
        >
          <div
            v-if="variantClass === 'mobile-swipe-variant'"
            class="modal-swipe"
            @mousedown="startDrag"
            @touchstart="startDrag"
          >
            <svg
              width="78"
              height="5"
              viewBox="0 0 78 5"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect
                width="78"
                height="5"
                rx="2.5"
                fill="#D1D1D1"
              />
            </svg>
          </div>
          <div
            v-if="!noHeader"
            ref="header"
            class="modal-header"
          >
            <button
              v-if="!isAlertDialog"
              class="close-cta"
              :aria-label="tc('booking.close')"
              @click.prevent.stop="deactivate"
            >
              <slot name="close">
                <span class="icon-close" />
              </slot>
            </button>
            <slot name="header" />
          </div>
          <div
            class="modal-body"
            :style="modalBodyStyle"
          >
            <slot
              name="body"
              v-bind="bindings"
            />
          </div>
          <div
            v-if="!noFooter"
            ref="footer"
            class="modal-footer"
          >
            <template v-if="customFooter">
              <slot
                name="footer"
                v-bind="bindings"
              />
            </template>
            <template v-else>
              <button
                v-if="ctaCloseText"
                class="close-cta"
                :disabled="ctaCloseDisabled || isLoading || !ctaCloseText"
                @click="deactivate"
              >
                {{ ctaCloseText }}
              </button>
              <button
                class="confirm-cta"
                :class="[{ loading: isLoading || !ctaText }]"
                :disabled="ctaDisabled || isLoading || !ctaText"
                @click="defaultAction"
              >
                {{ ctaText }}
              </button>
            </template>
          </div>
        </div>
        <template v-if="customBacklight">
          <slot
            name="backlight"
            v-bind="bindings"
          />
        </template>
        <template v-else>
          <div
            class="backlight"
            @click="backlightClick"
          />
        </template>
      </div>
    </transition>
  </div>
</template>

<script>
  import VariantMixin from '@mixins/variant-mixin'
  import TabTrapMixin from '@mixins/tab-trap'
  import ModalAccessibility from '@mixins/modal-accessibility'
  import { mapState, mapGetters } from 'vuex'

  export default {
    name: 'Modal',
    mixins: [
      VariantMixin(
        // Component name
        'modal',
        // Automatic generated computed properties
        ['variantClass',
         'transition',
         'getBodyHeight']
      ),
      TabTrapMixin,
      ModalAccessibility
    ],
    model: {
      prop: 'value',
      event: 'toggleDisplay'
    },
    props: {
      value: {
        type: Boolean
      },
      customClass: {
        type: String,
        default: ''
      },
      ctaText: {
        type: String,
        default: ''
      },
      ctaCloseText: {
        type: String,
        default: ''
      },
      ctaAction: {
        type: [Function, String, Object],
        default: null
      },
      ariaLabelledBy: {
        type: String,
        default: ''
      },
      ctaDisabled: Boolean,
      ctaCloseDisabled: Boolean,
      isPromise: Boolean,
      noFooter: Boolean,
      noHeader: Boolean,
      customFooter: Boolean,
      customBacklight: Boolean,
      reloadOnEscClose: Boolean,
      isAlertDialog: {
        type: Boolean,
        default: false
      },
    },
    data: () => ({
      isActive: false,
      isLoading: false,
      isMounted: false,
      innerActionPending: false,
      headerH: 0,
      footerH: 0,
      posY: 0,
      miscData: {},
    }),
    computed: {
      ...mapState('responsive', { wHeight: 'height', wWidth: 'width' }),
      ...mapGetters('i18n', ['tc']),
      bindings() {
        return {
          isActive: this.isActive,
          activate: this.activate,
          deactivate: this.deactivate,
          innerActionPending: this.innerActionPending,
          innerActionDone: this.innerActionDone,
          updateFocusable: this.updateFocusable,
          getFocusableElements: this.getFocusableElements,
          backlightClick: this.backlightClick,
          miscData: this.miscData
        }
      },
      bodyH() {
        return this.getBodyHeight(this.headerH, this.footerH, this.wHeight)
      },
      noInitialFocus() {
        return true
      },
      isInactive() {
        return !this.isActive
      },
      modalBodyStyle() {
        return {
          maxHeight: this.bodyH ? `${this.bodyH}px` : undefined,
          minHeight: this.variant === 'drawer' ? `${this.bodyH}px` : undefined
        }
      },
    },
    watch: {
      value(val) {
        if (val !== this.isActive) {
          this.isActive = val
        }
      },
      isActive(val) {
        if (this.value !== val) {
          this.$emit('input', val)
          this.$emit('toggleDisplay', val)
        }
        if (val) {
          this.getLayoutHeights()
        } else if (this.activatorEl) {
          this.activatorEl.focus()
        }
      },
      wWidth() {
        if (this.isActive) {
          this.getLayoutHeights()
        }
      },
      innerActionPending(val) {
        this.isLoading = val
      }
    },
    mounted() {
      this.isMounted = true
      this.$emit('mounted')
      this.$emit('toggleDisplay', this.isActive)
      if (this.value) {
        this.isActive = true
      }
    },
    methods: {
      activate(e, data) {
        if (e) this.innerActivator = e.currentTarget
        if (data) this.miscData = { ...data }
        this.isActive = true
        this.setPageContentAccessibility(true)
        document.body.classList.add('no-scroll')
        // Add focus management
        this.$nextTick(() => {
          this.updateFocusable()
        })
      },
      deactivate() {
        this.isActive = false
        this.$emit('close')
        this.setPageContentAccessibility(false)
        document.body.classList.remove('no-scroll')
      },
      backlightClick() {
        if (!this.isAlertDialog) {
          this.closeModal()
        }
      },
      closeModal() {
        this.deactivate()
        if (this.reloadOnEscClose) {
          setTimeout(() => {
            window.location.reload()
          }, 100)
        }
      },
      async defaultAction() {
        if (this.ctaAction) {
          if (typeof this.ctaAction === 'string') {
            if (this.ctaAction === 'internal') {
              this.innerActionPending = true
            } else {
              throw new Error('Unkown action')
            }
          } else if (typeof this.ctaAction.then === 'function' || this.isPromise) {
            this.isLoading = true
            await this.ctaAction()
            this.isLoading = false
          } else {
            this.ctaAction()
          }
        } else {
          this.deactivate()
        }
      },
      innerActionDone(error) {
        this.innerActionPending = false
        if (!error) {
          this.deactivate()
        }
      },
      getLayoutHeights() {
        this.$nextTick(() => {
          this.headerH = this.noHeader ? 0 : this.$refs.header.getBoundingClientRect().height
          this.footerH = this.noFooter ? 0 : this.$refs.footer.getBoundingClientRect().height
        })
      },
      updateFocusable() {
        this.getFocusableElements()
        this.$nextTick(() => {
          this.firstFocusableEl.focus()
        })
      },

      startDrag(event) {
        if (event.type === 'mousedown') {
          this.posY = event.clientY
          document.addEventListener('mousemove', this.doDrag)
          document.addEventListener('mouseup', this.stopDrag)
        } else if (event.type === 'touchstart') {
          this.posY = event.touches[0].clientY
          document.addEventListener('touchmove', this.doDrag)
          document.addEventListener('touchend', this.stopDrag)
        }
        document.body.style.userSelect = 'none'
      },
      doDrag(event) {
        if (event.type === 'mousemove') {
          this.posY = event.clientY
        } else if (event.type === 'touchmove') {
          this.posY = event.touches[0].clientY
        }
        if (this.posY - 107 <= 0) return
        this.$refs.modalWrapper.style.transform = `translate(${0}px, ${this.posY - 107}px)`
      },
      stopDrag() {
        document.removeEventListener('mousemove', this.doDrag)
        document.removeEventListener('mouseup', this.stopDrag)
        document.removeEventListener('touchmove', this.doDrag)
        document.removeEventListener('touchend', this.stopDrag)
        document.body.style.userSelect = ''
        if (this.posY >= (this.$refs.modalOverlay.offsetHeight * 0.5)) {
          this.deactivate()
        } else {
          this.$refs.modalWrapper.style.transform = ''
        }
      },
    }
  }
</script>
